import { useEffect, useRef, useState } from 'react';
import { ArrowUpIcon, NotePencilIcon, StopIcon, XIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { Button } from '@/ui/button/Button';

import { useImageAttachments } from '../../hooks/useImageAttachments';
import { useAgentSessionStore } from '../../stores/agent-session-store';
import { composeMessage, type Selection, useSelectionStore } from '../../stores/selection-store';
import { ComposerMenu } from '../ComposerMenu';
import { ImageThumbnails } from '../ImageAttachments';

import { usePopoverPanel } from '../../hooks/usePopoverPanel';

import { AssistantMarkdown } from './AssistantMarkdown';
import { MentionEditor, type MentionEditorHandle } from './composer/MentionEditor';
import { SELECTION_KIND } from './selection-kinds';

/** Where a selection's chip points, shown as the preview's header. */
function selectionHeader(selection: Selection): string {
  if (selection.kind === 'element') return selection.route;
  if (selection.kind === 'file') return selection.path;
  if (selection.kind === 'quote') return selection.role === 'assistant' ? 'Quoted from the agent' : 'Quoted from you';
  if (selection.kind === 'plan') return `Plan · v${selection.version}`;
  const { path, startLine, endLine } = selection;
  return startLine === endLine ? `${path} · line ${startLine}` : `${path} · lines ${startLine}-${endLine}`;
}

/**
 * One piece of context staged for the next message.
 *
 * Clicking the chip opens a panel showing exactly what will be sent — a rendered plan passage, a
 * code snippet, an element's markup — rather than just its bare path, with the note about it
 * editable underneath. That panel is the only way to annotate context attached from a menu rather
 * than from a selection (a whole file off the tree), and the only way to reword a note without
 * detaching and starting again.
 *
 * It is also where a note gets written when the *selection toolbar* asked for one. That gesture
 * happens across the page — in the thread, a diff, the plan — and hands the job here through
 * `annotating` rather than growing a second note field of its own out where the passage is.
 */
function PendingSelectionChip({
  selection,
  onRemove,
  onNote,
}: {
  selection: Selection;
  onRemove: () => void;
  onNote: (note: string) => void;
}) {
  const { open, show, toggle, close, triggerProps, panelProps } = usePopoverPanel({ align: 'left' });
  const [draft, setDraft] = useState(selection.note);
  const { Icon, said, prose } = SELECTION_KIND[selection.kind];
  const wanted = useSelectionStore((s) => s.annotating === selection.id);
  const setAnnotating = useSelectionStore((s) => s.setAnnotating);

  // Cleared as it opens — the handoff is one attach gesture, not a mode. Leaving it set would
  // reopen this panel every time the chip re-rendered, including right after it was dismissed.
  useEffect(() => {
    if (!wanted) return;
    setDraft(selection.note);
    show();
    setAnnotating(null);
  }, [wanted, selection.note, show, setAnnotating]);

  // Saves without closing. Blur fires for any click inside the panel — scrolling the preview,
  // selecting text in it — and a save that also dismissed the panel would make those gestures
  // close the thing they were aimed at.
  const commit = () => onNote(draft);

  return (
    <span
      className="relative flex max-w-64 items-center gap-1 rounded-full border border-brand-border bg-brand-tint py-0.5 pr-1 pl-2 text-2xs font-medium text-brand-text"
      {...triggerProps}
    >
      <button
        type="button"
        onClick={() => {
          // Re-seed from the store rather than trusting the local draft: the note may have been
          // rewritten since this chip last rendered its own copy.
          setDraft(selection.note);
          toggle();
        }}
        aria-label={selection.note ? `Edit ${said} on ${selection.label}` : `Add a ${said} to ${selection.label}`}
        className="flex min-w-0 items-center gap-1"
      >
        <Icon size={11} className="shrink-0" />
        {/* Prose is not a path — mono would only make it harder to read at chip size. */}
        <span className={`truncate ${prose ? 'italic' : 'font-mono'}`}>{selection.label}</span>
        {selection.note && <NotePencilIcon size={11} weight="fill" className="shrink-0" />}
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${selection.label}`}
        className="shrink-0 rounded-full p-0.5 hover:bg-brand/15"
      >
        <XIcon size={10} />
      </button>
      {open && (
        <div {...panelProps}>
          <div
            className={`max-w-[80vw] overflow-hidden rounded-xl border border-line-strong bg-surface-2 text-left shadow-(--shadow-overlay) ${
              // Prose needs the room: a palette table or a nested list at 320px wraps every cell
              // onto its own line, which is most of what made this read as a wall of text.
              prose ? 'w-104' : 'w-80'
            }`}
          >
            <div className="flex items-center gap-1.5 border-b border-line-subtle bg-surface-3 px-3 py-1.5 text-2xs font-medium text-fg-secondary">
              <Icon size={11} className="shrink-0" />
              <span className={`min-w-0 truncate ${prose ? '' : 'font-mono font-normal'}`}>
                {selectionHeader(selection)}
              </span>
            </div>

            {selection.kind !== 'file' && (
              <div className="max-h-64 overflow-auto px-3 py-2.5">
                {prose ? (
                  // Rendered, not quoted as source. The passage was picked out of a rendered
                  // document, so headings, tables and lists are how the user saw it and how they
                  // recognise it here. A selection that cuts a construct in half degrades to plain
                  // paragraphs, which is still easier to read than the raw markdown was.
                  <AssistantMarkdown content={selection.snippet} />
                ) : (
                  <pre className="text-2xs leading-relaxed font-normal whitespace-pre-wrap text-fg-secondary font-mono">
                    {selection.snippet}
                  </pre>
                )}
              </div>
            )}

            {/* Ruled off and labelled, because the two halves of this panel are different
                voices: above is what was picked, below is what the user says about it. Always
                editable — opening the panel at all is the gesture for annotating, so a
                read-only view with a second click to make it writable would only be in the way. */}
            <div className="border-t border-line-subtle px-3 py-2">
              <label htmlFor={`note-${selection.id}`} className="mb-1 block text-2xs font-medium text-fg-tertiary">
                Your {said}
              </label>
              <textarea
                id={`note-${selection.id}`}
                // The click that opened this panel is itself the request to type in it.
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  // Save before this bubbles to the panel's own Escape handler, which closes it.
                  if (e.key === 'Escape') commit();
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    commit();
                    close();
                  }
                }}
                rows={2}
                placeholder="What about it? (optional)"
                className="w-full resize-none rounded-lg bg-surface-1 px-2 py-1.5 text-xs font-normal text-fg placeholder:text-fg-tertiary focus:ring-1 focus:ring-brand focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

interface ComposerProps {
  /** Identifies the session's sandbox for the `@`-mention file search (`useFileTree`). */
  sessionId: string;
  turnInFlight: boolean;
  /** Returns false when the message couldn't be sent, so the draft can be preserved. */
  onSend: (message: string, images: string[]) => boolean;
  onAbort: () => void;
  disabled?: boolean;
  /**
   * The session's configured model — the source of truth for image support, since the proxy
   * resolves every request against it. Falls back to the model the agent reports when a session
   * has none of its own.
   */
  sessionModel?: string | null;
  /** Switches the live session's model; the proxy picks it up on the agent's next call. */
  onSelectModel: (id: string) => void;
  /** Opens the start-a-goal form; absent where goals do not run. */
  onStartGoal?: () => void;
  /**
   * Sandbox lifecycle line, rendered directly above the textarea in *every*
   * state — not just while disabled. It's the primary progress indicator for
   * provisioning and the only signal that a session has gone idle.
   */
  statusLine?: React.ReactNode;
  /**
   * Overrides the default prompt placeholder. Used to point at a question card above the
   * composer, so a user who ignores it still learns that typing here answers it.
   */
  placeholder?: string;
}

export function Composer({
  sessionId,
  turnInFlight,
  onSend,
  onAbort,
  disabled = false,
  sessionModel,
  onSelectModel,
  onStartGoal,
  statusLine,
  placeholder,
}: ComposerProps) {
  const editorRef = useRef<MentionEditorHandle>(null);
  const [empty, setEmpty] = useState(true);
  const selections = useSelectionStore((s) => s.selections);
  const removeSelection = useSelectionStore((s) => s.remove);
  const setSelectionNote = useSelectionStore((s) => s.setNote);
  const clearSelections = useSelectionStore((s) => s.clear);
  const liveModel = useAgentSessionStore((s) => s.model);

  // The agent reports whatever `LLM_PROXY_MODEL` its sandbox was provisioned with, which the
  // per-session model overrides on every proxied call — so the session's own model wins.
  const model = sessionModel ?? liveModel ?? null;
  const attachments = useImageAttachments(model);

  // Notes written against attached context are the user's words too, so a message made only of
  // annotated context — answering two quoted questions in place, say — is a complete message
  // with nothing left to type below it.
  const hasSomethingToSay = !empty || selections.some((s) => s.note);

  // Elements picked in the Preview tab travel as part of the prompt text — the
  // agent's transport has no attachment channel, and a selector is only useful to
  // it as something to read in the message.
  const submit = () => {
    const editor = editorRef.current;
    if (disabled || !editor || !hasSomethingToSay) return;
    const invalid = attachments.validate();
    if (invalid) {
      toast.error(invalid);
      return;
    }
    // Keep the draft if the socket was down — clearing it would silently lose what the user typed.
    if (!onSend(composeMessage(selections, editor.getText()), attachments.images)) return;
    editor.clear();
    attachments.clear();
    clearSelections();
  };

  return (
    <div
      className="flex shrink-0 flex-col gap-2 border-t border-line-subtle bg-surface-1 px-6 py-4"
      {...attachments.dropZoneProps}
    >
      {statusLine}
      {selections.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {selections.map((selection) => (
            <PendingSelectionChip
              key={selection.id}
              selection={selection}
              onRemove={() => removeSelection(selection.id)}
              onNote={(note) => setSelectionNote(selection.id, note)}
            />
          ))}
        </div>
      )}
      <ImageThumbnails images={attachments.images} onRemove={attachments.remove} />
      <div
        className={`flex flex-col gap-2 rounded-xl border bg-surface-2 p-2 ${
          attachments.dragging && attachments.canAttach ? 'border-brand-border' : 'border-line'
        }`}
      >
        <MentionEditor
          ref={editorRef}
          base={`/api/v1/agent-sessions/${sessionId}`}
          onSubmit={submit}
          onPasteFiles={(e) => attachments.onPaste(e as unknown as React.ClipboardEvent)}
          onEmptyChange={setEmpty}
          placeholder={
            placeholder ??
            (attachments.canAttach
              ? 'Ask the agent, or drop in a screenshot…'
              : 'Ask the agent to build features, fix bugs, or refine the plan…')
          }
          disabled={disabled}
          className="px-1 py-1"
        />
        <div className="flex items-center justify-between">
          <ComposerMenu
            inputRef={attachments.fileInputRef}
            onFileInputChange={attachments.onFileInputChange}
            canAttach={attachments.canAttach}
            model={model}
            onSelectModel={onSelectModel}
            onStartGoal={onStartGoal}
            disabled={disabled}
          />
          {turnInFlight ? (
            <Button variant="secondary" shape="square" size={9} onClick={onAbort} aria-label="Stop">
              <StopIcon size={15} weight="fill" />
            </Button>
          ) : (
            <Button
              variant="primary"
              shape="square"
              size={9}
              onClick={submit}
              disabled={disabled || !hasSomethingToSay}
              aria-label="Send"
            >
              <ArrowUpIcon size={16} weight="bold" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
